import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { ChecklistItemDisplay } from "@/components/animals/AdoptionListingChecklistItem";

interface EvaluationChecklistPendingItemProps {
  item: ChecklistItemDisplay;
  animalId: string;
  onResolved: () => void;
}

export function EvaluationChecklistPendingItem({
  item,
  animalId,
  onResolved,
}: EvaluationChecklistPendingItemProps) {
  const assignMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", `/api/animals/${animalId}/profile-checklist/${item.definitionId}/applicability`, {
        status: "assigned",
      }),
    onSuccess: onResolved,
  });

  const dismissMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", `/api/animals/${animalId}/profile-checklist/${item.definitionId}/applicability`, {
        status: "dismissed",
      }),
    onSuccess: onResolved,
  });

  const isPending = assignMutation.isPending || dismissMutation.isPending;

  return (
    <div
      className="flex items-start justify-between gap-3 py-2 px-3 rounded-md bg-muted/50"
      data-testid={`div-pending-item-${item.definitionId}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium" data-testid={`text-pending-item-name-${item.definitionId}`}>
          {item.name}
        </p>
        {item.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={() => assignMutation.mutate()}
          disabled={isPending}
          data-testid={`button-assign-pending-${item.definitionId}`}
        >
          {assignMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
          ) : null}
          Assign
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => dismissMutation.mutate()}
          disabled={isPending}
          data-testid={`button-dismiss-pending-${item.definitionId}`}
        >
          {dismissMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
          ) : null}
          N/A
        </Button>
      </div>
    </div>
  );
}
